import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileCheck, FileText, FileSignature, Receipt, Wallet, ClipboardList,
  CheckCircle2, ShieldCheck, Loader2, Eye, EyeOff, Copy, ExternalLink
} from 'lucide-react';
import { useAuth, formatApiError } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';

const MODULES = [
  { icon: FileText, label: 'Quotations', tint: 'bg-blue-100 text-blue-600' },
  { icon: FileSignature, label: 'Invoices', tint: 'bg-violet-100 text-violet-600' },
  { icon: Receipt, label: 'Receipts', tint: 'bg-pink-100 text-pink-600' },
  { icon: Wallet, label: 'Payment Vouchers', tint: 'bg-orange-100 text-orange-600' },
  { icon: ClipboardList, label: 'Proposals', tint: 'bg-emerald-100 text-emerald-600' },
];

export default function Login() {
  const { login, loginWithPassword, register, forgotPassword, user } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState('signin');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [signIn, setSignIn] = useState({ email: '', password: '' });
  const [signUp, setSignUp] = useState({ name: '', email: '', password: '' });
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetLink, setResetLink] = useState('');
  const [resetNotice, setResetNotice] = useState('');

  useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  const submitSignIn = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      await loginWithPassword(signIn.email.trim(), signIn.password);
      toast.success('Welcome back!');
      navigate('/', { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, 'Could not sign in. Please try again.'));
    } finally { setBusy(false); }
  };

  const submitSignUp = async (e) => {
    e.preventDefault();
    setError('');
    if (signUp.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setBusy(true);
    try {
      await register(signUp.name.trim(), signUp.email.trim(), signUp.password);
      toast.success('Account created — welcome to BizDocs!');
      navigate('/', { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, 'Could not create your account. Please try again.'));
    } finally { setBusy(false); }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setBusy(true); setResetLink(''); setResetNotice('');
    try {
      const data = await forgotPassword(forgotEmail.trim());
      if (data.reset_url) setResetLink(data.reset_url);
      else setResetNotice(data.message);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, 'Could not create a reset link'));
    } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Left brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-gradient-to-br from-blue-600 to-blue-700 p-12 text-white lg:flex">
        <div>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/20"><FileCheck className="h-6 w-6" /></div>
            <span className="text-xl font-bold">BizDocs Manager</span>
          </div>
          <h1 className="mt-16 max-w-md text-4xl font-extrabold leading-tight">Simple. Organised. Professional.</h1>
          <p className="mt-4 max-w-md text-blue-100">One simple app for all your business documents — quotations, invoices, receipts, vouchers and proposals. Built for Malaysian businesses.</p>
          <div className="mt-10 space-y-3">
            {['Create professional documents in one click', 'Track payments & outstanding balances', 'Ready for LHDN e-Invoice (Phase 2)'].map((t) => (
              <div key={t} className="flex items-center gap-2.5 text-sm text-blue-50"><CheckCircle2 className="h-5 w-5 shrink-0 text-blue-200" /> {t}</div>
            ))}
          </div>
        </div>
        <p className="text-sm text-blue-200">All your business documents. One app.</p>
      </div>

      {/* Right sign-in */}
      <div className="flex w-full flex-col items-center justify-center p-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600 text-white"><FileCheck className="h-6 w-6" /></div>
            <span className="text-xl font-bold text-slate-900">BizDocs Manager</span>
          </div>

          <h2 className="text-2xl font-bold text-slate-900">{tab === 'signin' ? 'Welcome back' : 'Create your account'}</h2>
          <p className="mt-1 text-sm text-slate-500">
            {tab === 'signin' ? 'Sign in to manage your business documents.' : 'Start issuing professional documents in minutes.'}
          </p>

          <Tabs value={tab} onValueChange={(v) => { setTab(v); setError(''); }} className="mt-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin" data-testid="tab-signin">Sign in</TabsTrigger>
              <TabsTrigger value="signup" data-testid="tab-signup">Sign up</TabsTrigger>
            </TabsList>

            <TabsContent value="signin">
              <form onSubmit={submitSignIn} className="mt-4 space-y-3.5">
                <div>
                  <Label htmlFor="si-email">Email</Label>
                  <Input id="si-email" type="email" required autoComplete="email" className="mt-1.5 h-11"
                    value={signIn.email} onChange={(e) => setSignIn({ ...signIn, email: e.target.value })}
                    placeholder="you@company.com.my" data-testid="signin-email" />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="si-pw">Password</Label>
                    <button type="button" onClick={() => { setForgotEmail(signIn.email); setResetLink(''); setResetNotice(''); setForgotOpen(true); }}
                      className="text-xs font-medium text-blue-600 hover:underline" data-testid="forgot-password-link">Forgot password?</button>
                  </div>
                  <div className="relative mt-1.5">
                    <Input id="si-pw" type={showPw ? 'text' : 'password'} required autoComplete="current-password" className="h-11 pr-10"
                      value={signIn.password} onChange={(e) => setSignIn({ ...signIn, password: e.target.value })}
                      placeholder="Your password" data-testid="signin-password" />
                    <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label="Toggle password">
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="auth-error">{error}</p>}
                <Button type="submit" disabled={busy} className="h-11 w-full bg-blue-600 hover:bg-blue-700" data-testid="signin-submit">
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Sign in
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={submitSignUp} className="mt-4 space-y-3.5">
                <div>
                  <Label htmlFor="su-name">Full Name</Label>
                  <Input id="su-name" required className="mt-1.5 h-11" value={signUp.name}
                    onChange={(e) => setSignUp({ ...signUp, name: e.target.value })} placeholder="Ahmad Faisal" data-testid="signup-name" />
                </div>
                <div>
                  <Label htmlFor="su-email">Email</Label>
                  <Input id="su-email" type="email" required autoComplete="email" className="mt-1.5 h-11" value={signUp.email}
                    onChange={(e) => setSignUp({ ...signUp, email: e.target.value })} placeholder="you@company.com.my" data-testid="signup-email" />
                </div>
                <div>
                  <Label htmlFor="su-pw">Password</Label>
                  <div className="relative mt-1.5">
                    <Input id="su-pw" type={showPw ? 'text' : 'password'} required autoComplete="new-password" className="h-11 pr-10"
                      value={signUp.password} onChange={(e) => setSignUp({ ...signUp, password: e.target.value })}
                      placeholder="At least 8 characters" data-testid="signup-password" />
                    <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label="Toggle password">
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="auth-error">{error}</p>}
                <Button type="submit" disabled={busy} className="h-11 w-full bg-blue-600 hover:bg-blue-700" data-testid="signup-submit">
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create account
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="my-5 flex items-center gap-3">
            <span className="h-px flex-1 bg-slate-200" /><span className="text-xs uppercase tracking-wide text-slate-400">or</span><span className="h-px flex-1 bg-slate-200" />
          </div>

          <Button onClick={login} data-testid="google-signin-btn" className="flex h-12 w-full items-center justify-center gap-3 border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50">
            <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            <span className="font-semibold">Continue with Google</span>
          </Button>

          <div className="mt-4 flex items-center justify-center gap-1.5 text-xs text-slate-400">
            <ShieldCheck className="h-3.5 w-3.5" /> Secured by Emergent Auth
          </div>

          <div className="mt-8">
            <p className="mb-3 text-xs font-medium uppercase tracking-wide text-slate-400">Everything in one place</p>
            <div className="flex flex-wrap gap-2">
              {MODULES.map((m) => (
                <div key={m.label} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
                  <div className={`flex h-7 w-7 items-center justify-center rounded-md ${m.tint}`}><m.icon className="h-4 w-4" /></div>
                  <span className="text-xs font-medium text-slate-600">{m.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent aria-describedby={undefined} className="max-w-md" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle>Reset your password</DialogTitle>
            <DialogDescription>We'll create a single-use link so you can set a new password.</DialogDescription>
          </DialogHeader>
          {!resetLink && !resetNotice ? (
            <form onSubmit={submitForgot} className="space-y-3">
              <p className="text-sm text-slate-500">Enter your account email and we'll create a reset link valid for 1 hour.</p>
              <div>
                <Label htmlFor="fp-email">Email</Label>
                <Input id="fp-email" type="email" required className="mt-1.5 h-11" value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)} placeholder="you@company.com.my" data-testid="forgot-email-input" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setForgotOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="forgot-submit">
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create reset link
                </Button>
              </DialogFooter>
            </form>
          ) : resetNotice ? (
            <div className="space-y-3" data-testid="reset-notice">
              <p className="text-sm text-slate-600">{resetNotice}</p>
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
                Email delivery is not connected yet, so the link is written to the server log for security.
                Ask your administrator for it, or connect an email service to have it delivered automatically.
              </p>
              <DialogFooter>
                <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => { setForgotOpen(false); setResetNotice(''); }} data-testid="reset-notice-close">Got it</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Email sending is not connected yet, so here is your reset link. It expires in 1 hour.
              </p>
              <p className="break-all rounded-lg bg-slate-100 px-3 py-2 font-mono text-xs text-slate-700" data-testid="reset-link-text">{resetLink}</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { navigator.clipboard.writeText(resetLink); toast.success('Link copied'); }} data-testid="copy-reset-link">
                  <Copy className="mr-1.5 h-4 w-4" /> Copy link
                </Button>
                <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => { window.location.href = resetLink; }} data-testid="open-reset-link">
                  <ExternalLink className="mr-1.5 h-4 w-4" /> Open link
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
